'use client';

import React from 'react';
import LandingNav from '@/app/components/LandingNav';
import LandingFooter from '@/app/components/LandingFooter';
import { CheckCircle2, Download, Monitor, AlertTriangle, Info } from 'lucide-react';
import { useTranslation } from '@/i18n';

export default function DesktopAppPage() {
  const { t, locale } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <main className="pt-24 pb-20 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8">

            <div className="lg:col-span-4 flex flex-col">
              <span className="rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider px-3 py-1 inline-flex items-center gap-2 w-fit">
                {t('publicWebsite:desktopApp.heroBadge') || (locale === 'ar' ? 'تطبيق سطح المكتب' : 'DESKTOP APP')}
              </span>
              <h1 className="text-4xl lg:text-5xl font-extrabold mt-4 mb-4">
                {locale === 'ar' ? 'تطبيق بروفلو 360 لسطح المكتب' : 'ProFlow 360 Desktop App'}
              </h1>
              <p className="text-lg text-muted-foreground max-w-xl">
                {locale === 'ar' ? 'إدارة خدمات PRO في الإمارات، والتأشيرات، والتراخيص التجارية، والعملاء الشرعيين، والامتثال اليومي مع تجربة سطح مكتب مركزة.' : 'Manage UAE PRO services, visas, trade licences, corporate clients, and everyday compliance with a focused desktop experience.'}
              </p>

              <div className="mt-6 flex flex-col gap-3">
                <div className="flex items-start gap-3 p-3 rounded-xl bg-white border border-border shadow-sm">
                  <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground/90">
                    {t('publicWebsite:desktopApp.benefits.oneClick') || (locale === 'ar' ? 'تنزيل بنقرة واحدة' : 'One-click download')}
                  </span>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-xl bg-white border border-border shadow-sm">
                  <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground/90">
                    {t('publicWebsite:desktopApp.benefits.alwaysUpToDate') || (locale === 'ar' ? 'محدّث دائماً' : 'Always up to date')}
                  </span>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-xl bg-white border border-border shadow-sm">
                  <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground/90">
                    {t('publicWebsite:desktopApp.benefits.secureReliable') || (locale === 'ar' ? 'آمن وموثوق' : 'Secure and reliable')}
                  </span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="rounded-3xl bg-white border border-border shadow-lg overflow-hidden p-8 flex flex-col gap-6 h-full">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-md">
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-8 h-8 text-white">
                      <path d="M3 5.557L10.455 4.5v7.023H3V5.557zM11.5 4.356L21 3v8.556h-9.5V4.356zM3 12.544h7.455v7.023L3 18.443v-5.899zM11.5 12.544H21V21l-9.5-1.21v-7.246z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center flex-wrap gap-2">
                      <h3 className="text-2xl font-bold text-foreground">{locale === 'ar' ? 'ويندوز' : 'Windows'}</h3>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-sky-100 text-sky-700 text-xs font-bold uppercase tracking-wide">
                        {locale === 'ar' ? 'متجر مايكروسوفت' : 'Microsoft Store'}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t('publicWebsite:desktopApp.windowsCard.description') || (locale === 'ar' ? 'ثبّت بروفلو 360 على ويندوز للحصول على تجربة سطح مكتب أسرع ومخصصة لسير العمل.' : 'Install ProFlow 360 on Windows for a faster, dedicated desktop workflow experience.')}
                </p>

                <div className="flex items-center gap-2 text-xs text-muted-foreground bg-slate-50 rounded-lg p-2.5 border border-border/80">
                  <Monitor className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {t('publicWebsite:desktopApp.windowsCard.versionLine') || (locale === 'ar' ? 'الإصدار 0.1.1 • تم التحديث أغسطس 2026' : 'Version 0.1.1 • Updated August 2026')}
                  </span>
                </div>

                <a
                  href="https://apps.microsoft.com/detail/9njqrjhqc3f2"
                  onClick={(e) => {
                    try {
                      window.location.href = 'ms-windows-store://pdp/?productid=9njqrjhqc3f2';
                      e.preventDefault();
                    } catch {}
                  }}
                  className="btn-primary w-full justify-center inline-flex items-center gap-2 py-3 font-semibold rounded-xl"
                >
                  <Download className="w-5 h-5" />
                  {t('publicWebsite:desktopApp.windowsCard.cta') || (locale === 'ar' ? 'تنزيل لويندوز' : 'Download for Windows')}
                </a>

                <div className="flex items-start gap-3 p-4 rounded-xl bg-sky-50 border border-sky-200 text-sky-900 text-sm">
                  <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>
                    {t('publicWebsite:desktopApp.windowsCard.storeNotice') || (locale === 'ar' ? 'متوفر مباشرة من خلال متجر مايكروسوفت الرسمي لويندوز 10 و 11.' : 'Available directly through the official Microsoft Store for Windows 10 & 11.')}
                  </span>
                </div>

                <div className="flex flex-col gap-3 mt-2">
                  <div className="flex items-center gap-3 text-sm text-foreground/90">
                    <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />
                    <span>{locale === 'ar' ? 'تثبيت رسمي من متجر مايكروسوفت' : 'Official Microsoft Store installation'}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-foreground/90">
                    <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />
                    <span>{locale === 'ar' ? 'تحديثات تلقائية في الخلفية' : 'Automatic background updates'}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-foreground/90">
                    <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />
                    <span>{locale === 'ar' ? 'إشعارات صينية أصلية للنظام' : 'Native system tray notifications'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="rounded-3xl bg-white border border-border shadow-lg overflow-hidden p-8 flex flex-col gap-6 h-full">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-800 to-neutral-900 flex items-center justify-center shadow-md">
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-8 h-8 text-white">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center flex-wrap gap-2">
                      <h3 className="text-2xl font-bold text-foreground">{locale === 'ar' ? 'ماك أو إس' : 'macOS'}</h3>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-wide">
                        {locale === 'ar' ? 'وصول مبكر' : 'Early Access'}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t('publicWebsite:desktopApp.macCard.description') || (locale === 'ar' ? 'استخدم بروفلو 360 على نظام ماك مع تجربة سطح مكتب مركزة وعالية الأداء لخدمات الأعمال.' : 'Use ProFlow 360 on macOS with a focused, high-performance desktop experience for business services.')}
                </p>

                <div className="flex items-center gap-2 text-xs text-muted-foreground bg-slate-50 rounded-lg p-2.5 border border-border/80">
                  <Monitor className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {t('publicWebsite:desktopApp.macCard.versionLine') || (locale === 'ar' ? 'الإصدار 0.1.1 • تم التحديث أغسطس 2026' : 'Version 0.1.1 • Updated August 2026')}
                  </span>
                </div>

                <a
                  href="https://github.com/mzawan-MC1/proflow360/releases/download/v8f965e2/ProFlow.360_0.1.0_universal.dmg"
                  download
                  className="btn-accent w-full justify-center inline-flex items-center gap-2 py-3 font-semibold rounded-xl"
                >
                  <Download className="w-5 h-5" />
                  {t('publicWebsite:desktopApp.macCard.cta') || (locale === 'ar' ? 'تنزيل لماك أو إس' : 'Download for macOS')}
                </a>

                <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>
                    {t('publicWebsite:desktopApp.macCard.notarizationWarning') || (locale === 'ar' ? 'هذا الإصدار من وصول مبكر لمعالجات Apple Silicon لم يتم تصديقه من Apple حتى الآن. للفتح على نظام ماك: انقر بزر الماوس الأيمن على التطبيق > افتح.' : 'This Apple Silicon early-access build is not yet Apple-notarized. To open on macOS: Right-click the app > Open.')}
                  </span>
                </div>

                <div className="flex flex-col gap-3 mt-2">
                  <div className="flex items-center gap-3 text-sm text-foreground/90">
                    <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />
                    <span>{locale === 'ar' ? 'تثبيت بسيط عبر ملف DMG' : 'Simple DMG installation'}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-foreground/90">
                    <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />
                    <span>{locale === 'ar' ? 'مزامنة حساب سطح المكتب' : 'Desktop account synchronization'}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-foreground/90">
                    <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />
                    <span>{locale === 'ar' ? 'دعم أصلي للغة العربية (من اليمين إلى اليسار) والإنجليزية' : 'Native Arabic (RTL) and English support'}</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
}
